/**
 * Busca + ordenacao das listagens de cadastros da averbadora (bancos e
 * prefeituras). Fonte unica pra que as duas telas se comportem igual.
 *
 * Busca: RESTRITA a razao social / nome fantasia / CNPJ. Nao usa matchAny
 * (que varre todos os primitivos) de proposito — sem a restricao, termos como
 * "sandbox", "ativo" ou um dominio de e-mail retornariam a base inteira.
 * O casamento em si (acento, caixa, CNPJ com ou sem mascara) fica por conta
 * do matchAnyKeys — ver text-search.ts.
 *
 * Ordem: cadastros pendentes (esteira do site institucional, aguardando
 * aprovacao do operador) sempre no topo; o resto alfabetico. Ao aprovar, o
 * status deixa de ser "pendente" e o registro cai sozinho no bloco A-Z.
 */

import type { CSSProperties } from "react";
import { matchAnyKeys } from "./text-search";

/**
 * Situacoes de um cadastro (banco ou prefeitura), como chips de filtro.
 *
 * Espelha `TENANT_STATUS` da API (apps/api/src/modules/admin/index.ts). Lista
 * fixa, e nao facetas vindas da rota: ao contrario de `vinculo`/`situacao` dos
 * servidores — strings LIVRES que o import por CSV so faz cast — a coluna
 * `status` de banco/prefeitura so recebe valor deste conjunto, escrito pelo
 * proprio formulario. Faceta aqui seria consulta a mais para descobrir algo que
 * ja se sabe.
 *
 * SEM CONTAGEM: o filtro vai para a API, e a contagem honesta teria de sair da
 * base com todos os OUTROS filtros aplicados menos o da propria dimensao
 * (FR-010) — semantica que estas rotas nao expoem. Chip com numero errado seria
 * pior que chip sem numero.
 */
export const SITUACAO_CADASTRO_OPCOES: { value: string; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "ativo", label: "Ativo" },
  { value: "pendente", label: "Pendente" },
  { value: "pausado", label: "Pausado" },
  { value: "inativo", label: "Inativo" },
];

/** Grade dos chips dentro da `FilterBar` — o mesmo layout que as demais telas
 *  com filtro rapido ja usam. */
export const GRADE_FILTROS: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};

/** Shape comum de AdminBanco e AdminPrefeitura usado aqui. */
type Cadastro = {
  nome: string;
  razaoSocial?: string;
  nomeFantasia?: string;
  cnpj?: string;
  /** Feature 009: a sigla e' como uma camara ou autarquia se chama, e a lista
   *  de orgaos promete busca-la no placeholder. Opcional porque banco nao tem. */
  sigla?: string | null;
  status: string;
};

/**
 * ⚠️ Filtro no CLIENTE — restou apenas onde a listagem ainda nao pagina no
 * servidor. Bancos e Prefeituras sairam daqui na feature 007: a busca e a
 * ordem viraram `predicadoDeBusca` e `ORDEM_CADASTRO` em
 * `apps/api/src/db/consultas-averbadora.ts`, sobre as MESMAS chaves.
 *
 * Nao volte a usar esta funcao numa tela paginada no servidor: filtrar aqui
 * depois de receber uma pagina faria a busca procurar so dentro dela.
 */
export function filtrarOrdenarCadastros<T extends Cadastro>(lista: T[], query: string): T[] {
  return lista
    .filter((r) => matchAnyKeys(r, query, ["nome", "razaoSocial", "nomeFantasia", "cnpj", "sigla"]))
    // slice() antes do sort: o array vem do cache do TanStack Query e sort
    // muta in-place.
    .slice()
    .sort((a, b) => {
      const pendenteA = a.status === "pendente" ? 0 : 1;
      const pendenteB = b.status === "pendente" ? 0 : 1;
      if (pendenteA !== pendenteB) return pendenteA - pendenteB;
      // Ordena por `nome` — e' a coluna VISIVEL nas duas tabelas. Ordenar por
      // razaoSocial (opcional, vazia em varios registros) deixaria a lista
      // parecendo desordenada na tela.
      return (a.nome ?? "").localeCompare(b.nome ?? "", "pt-BR", { sensitivity: "base" });
    });
}
